import { FeatureEnum } from "@/openapi";
import { useChild } from "./child-helper";
import { useMe } from "./me-context";
import { useEffect, useMemo, useState } from "react";
import { useIsSecondaryCaregiver } from "@/routes/account/hooks";

export interface Permissions {
  read: boolean,
  create: boolean,
  update: boolean,
  delete: boolean
}

const emptyPermission: Permissions = { read: false, create: false, update: false, delete: false };
const fullPermission: Permissions = { read: true, create: true, update: true, delete: true };

/*
 * NOT INTENDED FOR DIRECT USEAGE
 * Custom hook to check what actions the current user has permissions for
 * If no child or me is available, all permissions are false.
 * If the user is the primary caregiver, all permissions are true.
 * If no permissions are granted,  all permissions are false.
 */
export const useGetPermissionActions = (feature: FeatureEnum): Permissions => {
  const child = useChild();
  const me = useMe();

  return useMemo(() => {
    if (!child || !me) return emptyPermission;

    // Primary caregiver has all permissions
    if (child.caregiverId === me.attributes.sub) return fullPermission;

    if (child.receivedPermissions === undefined) return fullPermission;

    const permissions = child.receivedPermissions ?? [];

    if (permissions.length === 0) return emptyPermission;

    const featurePermission = permissions.find((permission) => permission.feature === feature);

    if (!featurePermission) return emptyPermission;

    const result = {...emptyPermission};

    featurePermission.actions.split("").forEach((action) => {
      switch (action.toLocaleUpperCase()) {
        case "R": 
          result.read = true;
          break;
        case "C":
          result.create = true;
          break;
        case "U":
          result.update = true;
          break;
        case "D":
          result.delete = true;
          break;
      }
    });

    return result;
  }, [child, me, feature]);
}

const useDefaultPermissions = (feature: FeatureEnum) => {
  const { read, create, update, delete: del } = useGetPermissionActions(feature);

  const hasAny = useMemo(() => { 
    return read || create || update || del;
  }, [read, create, update, del]);

  return useMemo(
    () => ({
      canView: read,
      canEdit: update && create && read && del,
      noAccess: !hasAny,
    }),
    [read, create, update, del, hasAny],
  );
}

// Similar to default permissions except "canEdit" does not include "delete" as that action is not relevant
const useNoDelInEditPermissions = (feature: FeatureEnum) => {
  const { read, create, update, delete: del } = useGetPermissionActions(feature);

  const hasAny = useMemo(() => { 
    return read || create || update || del;
  }, [read, create, update, del]);

  return useMemo(
    () => ({
      canView: read,
      canEdit: update && create && read,
      noAccess: !hasAny,
    }),
    [read, create, update, hasAny],
  );
}

export const useLocationPermissions = () => {
  return useNoDelInEditPermissions(FeatureEnum.Location);
};

export const useContactPermissions = () => {
  return useDefaultPermissions(FeatureEnum.Contact);
};

export const usePhoneHistoryPermissions = () => {
  return useDefaultPermissions(FeatureEnum.PhoneHistory);
};

export const useScheduleOverridePermissions = () => {
  const feature = FeatureEnum.ScheduleOverride;

  const { read, create, update, delete: del } = useGetPermissionActions(feature);

  const hasAny = useMemo(() => { 
    return read || create || update || del;
  }, [read, create, update, del]);

  return useMemo(
    () => ({
      noAccess: !hasAny,
    }),
    [hasAny],
  );
};

export const usePinwheelAskPermissions = () => {
  return useDefaultPermissions(FeatureEnum.PinwheelGpt);
};

export const useSafelistSettingPermissions = () => {
  const feature = FeatureEnum.SafelistSetting;

  const { update } = useGetPermissionActions(feature);

  return useMemo(
    () => ({
      canEdit: update,
    }),
    [update],
  );
};

export const useAppsScheduleModesRoutinesPermissions = () => {
  // We assume that the actions for Apps, Schedule, Modes, and Routines are the same.
  // If they differ, we can create separate hooks for each feature.
  // For now, we use the same actions for all.
  // This can be adjusted based on the actual requirements.
  return useNoDelInEditPermissions(FeatureEnum.App);
};

interface UseAccessRedirectProps {
  shouldBlock: boolean;
  redirectDelay?: number;
}

const useAccessRedirect = ({ shouldBlock, redirectDelay = 2000 }: UseAccessRedirectProps) => {
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (!shouldBlock) {
      setShowRedirectMessage(false);
      setShouldRedirect(false);
      return;
    }

    setShowRedirectMessage(true);

    const redirectTimer = setTimeout(() => {
      setShouldRedirect(true);
    }, redirectDelay);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, [shouldBlock, redirectDelay]);

  return useMemo(
    () => ({
      shouldRedirect,
      showRedirectMessage: shouldBlock && showRedirectMessage && !shouldRedirect,
      isLoading: shouldBlock && !shouldRedirect && !showRedirectMessage,
    }),
    [shouldRedirect, showRedirectMessage, shouldBlock],
  );
};

interface UsePermissionRedirectProps {
  noAccess: boolean;
  redirectDelay?: number;
}

export const usePermissionRedirect = ({ noAccess, redirectDelay = 2000 }: UsePermissionRedirectProps) => {
  const isSecondaryCaregiver = useIsSecondaryCaregiver();
  const shouldBlock = useMemo(() => isSecondaryCaregiver && noAccess, [isSecondaryCaregiver, noAccess]);
  
  return useAccessRedirect({ shouldBlock, redirectDelay });
};

interface UsePinwheelHomeChildRedirectProps {
  redirectDelay?: number;
  isPinwheelHomeChild: boolean;
}

// Default landing page for a PW Home child, e.g. when redirecting away from a route that doesn't apply to PW Home.
export const PINWHEEL_HOME_DEFAULT_ROUTE = "/phone-history";

export const usePinwheelHomeChildRedirect = ({ redirectDelay = 2000, isPinwheelHomeChild }: UsePinwheelHomeChildRedirectProps) => {
  return useAccessRedirect({ shouldBlock: isPinwheelHomeChild, redirectDelay });
};

